import ButtonComponent from 'app_components/commons/Button/ButtonComponent'
import TextComponent from 'app_components/commons/TextComponent/TextComponent'
import React, { FC, Fragment, useCallback, useMemo, useState } from 'react'
import StudentDashboardSchedulingTable from '../StudentDashboardSchedulingTable/StudentDashboardSchedulingTable'

import './StudentDashboardCourseInformation.scss'
import StudentDashboardActivitiesTable from '../StudentDashboardActivitiesTable/StudentDashboardActivitiesTable';
import StudentDashboardSurveysTable from '../StudentDashboardSurveysTable/StudentDashboardSurveysTable'
import TimezoneService from 'app_services/user/timezoneService'
import StudentDashboardSchedulingHistoryTable from '../StudentDashboardSchedulingHistoryTable/StudentDashboardSchedulingHistoryTable';
import { STUDENT_DASHBOARD_TAB } from '../../StudentDashboardPage'
import useWindowWidth from 'app_hooks/useWindowWidth'
import Slider from 'react-slick'
import ChipButton from 'app_components/commons/ChipButton/ChipButton'
import { GoCalendar } from 'react-icons/go'
import CourseSchedulingService from 'app_services/admin/courseScheduling/courseSchedulingService'
import TooltipComponent from 'app_components/commons/TooltipComponent/TooltipComponent'
import { useColors } from 'app_hooks/useColors'
import { useSelector } from 'react-redux';
import { CombinedReducers } from 'app_events/index'

export enum StudentDashboardCourseInformationMode {
  SCHEDULING = 'scheduling',
  ACTIVITIES = 'activities',
  SERVICE = 'service',
  HISTORY = 'history',
}

const MODE_NAMES = {
  [StudentDashboardCourseInformationMode.SCHEDULING]: 'Programación e ingreso',
  [StudentDashboardCourseInformationMode.ACTIVITIES]: 'Evaluación y exámenes',
  [StudentDashboardCourseInformationMode.SERVICE]: 'Evalúe nuestro servicio',
  [StudentDashboardCourseInformationMode.HISTORY]: ((isVirtual: boolean) => isVirtual ? 'Histórico de módulos' : 'Histórico de sesiones') as (isVirtual: boolean) => string,
}

const MOBILE_BREAKPOINT = 800

interface IStudentDashboardCourseInformationProps {
  modes: StudentDashboardCourseInformationMode[]
  tab?: STUDENT_DASHBOARD_TAB
}

const StudentDashboardCourseInformation: FC<IStudentDashboardCourseInformationProps> = ({ modes, tab }) => {
  const [currentMode, setCurrentMode] = useState<StudentDashboardCourseInformationMode | undefined>(modes.length ? modes[0] : undefined)
  const { windowWidth } = useWindowWidth()
  const [colors] = useColors()
  const { courseSelected } = useSelector((state: CombinedReducers) => state.courses)
  const isVirtual = useMemo(() => courseSelected?.schedulingMode?.name === 'Virtual', [courseSelected])

  const currentTimezone = useMemo(() => {
    const timezoneService = new TimezoneService()
    return timezoneService.getCurrentTimezone()
  }, [])

  const getActiveClass = useCallback((mode: StudentDashboardCourseInformationMode) => {
    if (currentMode === mode) {
      return 'active'
    }
    return ''
  }, [currentMode])

  const handleClickMode = useCallback((mode: StudentDashboardCourseInformationMode) => {
    setCurrentMode(mode)
  }, [])

  const handleCalendar = async () => {
    const courseSchedulingService = new CourseSchedulingService()
    await courseSchedulingService.downloadCalendar({
      service_id: courseSelected?.service_id,
    });
  }

  const getTabName = useCallback((mode: StudentDashboardCourseInformationMode) => {
    return typeof MODE_NAMES[mode] === 'string' ? MODE_NAMES[mode] : (MODE_NAMES[mode] as (isVirtual: boolean) => string)(isVirtual)
  }, [isVirtual])

  return (
    <div className="student-dashboard-course-information--layout">
      {windowWidth && windowWidth > MOBILE_BREAKPOINT ? (
        <div className="student-dashboard-course-information--buttons">
          {modes?.map((mode) => (
            <ButtonComponent id={`${mode}-button`} key={mode} className={getActiveClass(mode)} onClick={() => handleClickMode(mode)}>
              {getTabName(mode)}
            </ButtonComponent>
          ))}
        </div>
      ) : (
        <div style={{ width: 'calc(100% - 52px)', margin: '0px auto' }}>
          <Slider
            dots={false}
            adaptiveHeight={true}
            infinite={false}
            arrows
            autoplay={false}
            draggable={false}
            autoplaySpeed={4000}
            speed={500}
            slidesToShow={1}
            slidesToScroll={1}
            className="student-dashboard-course-information--slider"
          >
            {modes?.map((mode) => (
              <div key={mode}>
                <ChipButton
                  active={currentMode === mode}
                  id={`${mode}-button`}
                  onClick={() => handleClickMode(mode)}
                  width="100%"
                >
                  {getTabName(mode)}
                </ChipButton>
              </div>
            ))}
          </Slider>
        </div>
      )}
      {!!currentMode && (
        <Fragment>
          <div className='student-dashboard-course-information--title'>
            <TextComponent fontSize="1.125rem" color="#42474E">{getTabName(currentMode)}</TextComponent>
            {[StudentDashboardCourseInformationMode.SCHEDULING, StudentDashboardCourseInformationMode.HISTORY].includes(currentMode) && (
              <TextComponent fontSize="1rem" color="#42474E"><strong>Zona horaria: </strong>{currentTimezone?.label}</TextComponent>
            )}
            {!isVirtual && currentMode === StudentDashboardCourseInformationMode.SCHEDULING && (
              <TooltipComponent text='Descarga y sincroniza calendario' >
                <ButtonComponent className='student-dashboard-course-information--calendar' onClick={handleCalendar} style={{ padding: '0.4rem 0.6rem' }}>
                  <ButtonComponent.Icon>
                    <GoCalendar size="25px" color={colors.primary} />
                  </ButtonComponent.Icon>
                </ButtonComponent>
              </TooltipComponent>
            )}
          </div>
          {currentMode === StudentDashboardCourseInformationMode.SCHEDULING && (
            <StudentDashboardSchedulingTable id={`${StudentDashboardCourseInformationMode.SCHEDULING}-table`} />
          )}
          {currentMode === StudentDashboardCourseInformationMode.HISTORY && (
            <StudentDashboardSchedulingHistoryTable id={`${StudentDashboardCourseInformationMode.HISTORY}-table`} />
          )}
          {currentMode === StudentDashboardCourseInformationMode.ACTIVITIES && (
            <StudentDashboardActivitiesTable tab={tab} id={`${StudentDashboardCourseInformationMode.ACTIVITIES}-table`} />
          )}
          {currentMode === StudentDashboardCourseInformationMode.SERVICE && (
            <StudentDashboardSurveysTable id={`${StudentDashboardCourseInformationMode.SERVICE}-table`} />
          )}
        </Fragment>
      )}
    </div>
  )
}

export default StudentDashboardCourseInformation
